import axios from 'axios';
import React from 'react';
import { environment } from '../../environment';
import { errorCatcher, useSnackbar } from '../../hooks';
import { useLogin } from '../login';
import { ITrackableHistory, ITrackableHistoryQuery } from './types';

type UseTrackableHistory = [ITrackableHistory | null, boolean, {
  load: (trackableItemId: string, query?: ITrackableHistoryQuery) => Promise<ITrackableHistory | null>;
}];

// #region buildParams
const buildParams = (query: ITrackableHistoryQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  if (query.from) params.from = query.from;
  if (query.to) params.to = query.to;
  if (query.reasons && query.reasons.length > 0) params.reason = query.reasons.join(',');
  if (query.zoneId) params.zone_id = query.zoneId;
  if (query.limit != null) params.limit = query.limit;
  if (query.offset != null) params.offset = query.offset;
  return params;
};

// #region useTrackableHistory
export const useTrackableHistory = (): UseTrackableHistory => {
  const [history, setHistory] = React.useState<ITrackableHistory | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();
  const requestId = React.useRef(0);

  const load = async (trackableItemId: string, query: ITrackableHistoryQuery = {}) => {
    const current = ++requestId.current;
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${environment.baseUrl}/api/v2/inventory/trackable-items/${encodeURIComponent(trackableItemId)}/history`,
        { params: buildParams(query), headers: { 'X-Wallmates-Auth': authToken } }
      );
      // Ignore stale responses when filters change quickly
      if (current !== requestId.current) return null;
      setHistory(data as ITrackableHistory);
      return data as ITrackableHistory;
    } catch (e: any) {
      if (current !== requestId.current) return null;
      errorCatcher(e, loginFunctions.logout);
      setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to load stock history.' });
      return null;
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  };

  return [history, loading, { load }];
};
